import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { isScopedCashExecution } from '../presentation/rebalanceModePresentation';
import {
  hasAuditLiquidationTrigger,
  hasLiquidateTrigger,
  hasTransferTrigger,
} from '../presentation/reportLabels';
import { buildTradeActivitySummary } from '../snapshot/buildTradeActivitySummary';
import { OptimizerDecisionStage } from './advisorSituationKeys';
import { isZeroCashModeApplication } from './formatAdvisorInsightText';
import {
  parseCashDeployCents,
  parseCashRaiseCents,
} from './optimizerDecisionActionPatterns';

export type RebalanceTrigger = 'drift' | 'liquidate' | 'transfer';

export const resolveRebalanceInsightTriggers = (
  report: PortfolioReport | undefined,
  audit: OptimizerAuditV2Record | undefined,
): RebalanceTrigger[] => {
  const scopedCash = isScopedCashExecution(audit);
  const triggers: RebalanceTrigger[] = [];

  if (!scopedCash && report?.driftRebalanceRequired) {
    triggers.push('drift');
  }

  if (scopedCash) {
    if (hasAuditLiquidationTrigger(audit)) {
      triggers.push('liquidate');
    }
  } else if (hasLiquidateTrigger(report, audit)) {
    triggers.push('liquidate');
  }

  if (hasTransferTrigger(report, audit)) {
    triggers.push('transfer');
  }

  return triggers;
};

export type RebalanceChangeSummary = {
  triggers: RebalanceTrigger[];
  cashRaiseCents: number;
  cashDeployCents: number;
  displayCashRaiseCents: number;
  displayCashDeployCents: number;
  isScopedCash: boolean;
  hasMixedSleeveCash: boolean;
  isCashEvent: boolean;
  newPositions: string[];
  exits: string[];
  tradedHoldingCount: number;
  hasChanges: boolean;
};

const sumExecutedCashModeCentsFromAudit = (
  audit?: OptimizerAuditV2Record,
): { raiseCents: number; deployCents: number } => {
  let raiseCents = 0;
  let deployCents = 0;

  for (const decision of audit?.decisions ?? []) {
    if (decision.stage !== OptimizerDecisionStage.RebalanceMode) {
      continue;
    }

    if (isZeroCashModeApplication(decision.action)) {
      continue;
    }

    const raised = parseCashRaiseCents(decision.action);
    const deployed = parseCashDeployCents(decision.action);

    if (raised != null) {
      raiseCents += raised;
    }

    if (deployed != null) {
      deployCents += deployed;
    }
  }

  return { raiseCents, deployCents };
};

const sumSubAccountField = (
  report: PortfolioReport | undefined,
  field: 'pendingWithdrawCents' | 'pendingContributionCents',
): number =>
  (report?.subAccounts ?? []).reduce(
    (sum, subAccount) => sum + Math.max(0, subAccount?.[field] ?? 0),
    0,
  );

/**
 * Distils the advisor-facing "what's changing & why" story for one portfolio:
 * the rebalance trigger, any cash raise/deploy driving it, and the securities
 * entering or leaving the sleeves.
 */
export const buildRebalanceChangeSummary = (
  report: PortfolioReport | undefined,
  diffRows: WeightDiffDetailRow[],
  audit: OptimizerAuditV2Record | undefined,
): RebalanceChangeSummary => {
  const tradeActivity = buildTradeActivitySummary(diffRows, audit);

  const triggers = resolveRebalanceInsightTriggers(report, audit);

  const cashRaiseCents = sumSubAccountField(report, 'pendingWithdrawCents');
  const cashDeployCents = sumSubAccountField(
    report,
    'pendingContributionCents',
  );
  const executedCash = sumExecutedCashModeCentsFromAudit(audit);
  const displayCashRaiseCents =
    cashRaiseCents > 0 ? cashRaiseCents : executedCash.raiseCents;
  const displayCashDeployCents =
    cashDeployCents > 0 ? cashDeployCents : executedCash.deployCents;
  const isScopedCash = isScopedCashExecution(audit);
  const hasRaiseSignal = displayCashRaiseCents > 0;
  const hasDeploySignal = displayCashDeployCents > 0;
  const hasMixedSleeveCash = isScopedCash && hasRaiseSignal && hasDeploySignal;

  const newPositions = tradeActivity?.entries.map((row) => row.label) ?? [];
  const exits = tradeActivity?.exits.map((row) => row.label) ?? [];
  const tradedHoldingCount = diffRows.filter(
    (row) => row.deltaCents !== 0,
  ).length;

  const hasChanges =
    triggers.length > 0 ||
    cashRaiseCents > 0 ||
    cashDeployCents > 0 ||
    newPositions.length > 0 ||
    exits.length > 0;

  return {
    triggers,
    cashRaiseCents,
    cashDeployCents,
    displayCashRaiseCents,
    displayCashDeployCents,
    isScopedCash,
    hasMixedSleeveCash,
    isCashEvent:
      triggers.includes('liquidate') || triggers.includes('transfer'),
    newPositions,
    exits,
    tradedHoldingCount,
    hasChanges,
  };
};
